import React, { useState, useEffect, useRef } from "react";
import {
  X,
  Save,
  SquarePen,
  Paperclip,
  Languages,
  Sparkles,
  Printer,
  Loader2,
  Send,
  ArrowLeft,
  FileClock,
  Eye,
  EyeOff,
} from "lucide-react";
import { toast } from "sonner";
import * as api from "../api";
import { AiRagiona } from "./AiRagiona";
import EditorRicco, { testoAHtml, htmlVuoto } from "./EditorRicco";

// NOTA: Traduci e AI sono simulati (mock). Verranno collegati all'AI reale
// (chiave Emergent LLM) nella fase backend.

// Il corpo contiene, in coda, firma ("--") e cronologia ("---------- ..."): l'AI lavora solo sul testo scritto dall'utente
const SEP_FIRMA = "\n--\n";
const SEP_CRONO = "\n---------- ";
function spezzaCorpo(corpo) {
  const t = corpo || "";
  const idx = [t.indexOf(SEP_FIRMA), t.indexOf(SEP_CRONO)].filter((i) => i >= 0);
  if (idx.length === 0) return { testo: t, resto: "" };
  const i = Math.min(...idx);
  return { testo: t.slice(0, i), resto: t.slice(i) };
}
// (la maiuscola automatica a inizio frase è gestita dentro EditorRicco)
const bloccoFirma = (f) => (f?.firma_testo ? `\n\n--\n${f.firma_testo.trim()}` : "");

function formatSize(bytes) {
  if (bytes < 1024) return bytes + " B";
  if (bytes < 1024 * 1024) return Math.round(bytes / 1024) + " KB";
  return (bytes / (1024 * 1024)).toFixed(1) + " MB";
}

// Campo destinatari multipli con chip (A, CC, CCN) + suggerimenti dalla rubrica mentre si digita
function RecipientField({ label, chips, setChips, value, setValue, placeholder, testid, extra }) {
  const [sugg, setSugg] = useState([]);
  const [idx, setIdx] = useState(-1);
  const timer = useRef(null);
  const ultimaQ = useRef("");

  useEffect(() => {
    const q = value.trim();
    clearTimeout(timer.current);
    if (q.length < 1) {
      setSugg([]);
      return;
    }
    timer.current = setTimeout(() => {
      ultimaQ.current = q;
      api
        .cercaContatti(q)
        .then((r) => {
          if (ultimaQ.current !== q) return;
          setSugg(r.filter((c) => !chips.includes(c.email)));
          setIdx(-1);
        })
        .catch(() => setSugg([]));
    }, 180);
    return () => clearTimeout(timer.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const aggiungi = (addr) => {
    if (!chips.includes(addr)) setChips([...chips, addr]);
    setValue("");
    setSugg([]);
    setIdx(-1);
  };

  const commit = (silent) => {
    const v = value.trim().replace(/[,;\s]+$/, "");
    if (!v) return;
    if (!v.includes("@")) {
      if (!silent) toast.error("Indirizzo non valido: " + v);
      return;
    }
    aggiungi(v);
  };

  const onKey = (e) => {
    if (e.key === "ArrowDown" && sugg.length) {
      e.preventDefault();
      setIdx((i) => (i + 1) % sugg.length);
    } else if (e.key === "ArrowUp" && sugg.length) {
      e.preventDefault();
      setIdx((i) => (i <= 0 ? sugg.length - 1 : i - 1));
    } else if (e.key === "Escape") {
      setSugg([]);
    } else if (e.key === "Enter" || e.key === "," || e.key === ";" || e.key === " ") {
      e.preventDefault();
      if (sugg.length && (idx >= 0 || (e.key === "Enter" && !value.includes("@")))) aggiungi(sugg[Math.max(idx, 0)].email);
      else commit(false);
    } else if (e.key === "Backspace" && !value && chips.length > 0) {
      setChips(chips.slice(0, -1));
    }
  };

  return (
    <div className="wf-field">
      <div className="wf-label-row">
        <label className="wf-label">{label}</label>
        {extra}
      </div>
      <div className="recipient-box anchor-wrap">
        {chips.map((c, i) => (
          <span key={`${c}-${i}`} className="attach-chip" data-testid={`${testid}-chip-${i}`}>
            <span className="attach-chip-name">{c}</span>
            <button
              className="attach-chip-x"
              onClick={() => setChips(chips.filter((_, j) => j !== i))}
              aria-label={"Rimuovi " + c}
            >
              <X size={13} strokeWidth={2.6} />
            </button>
          </span>
        ))}
        <input
          className="recipient-input"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={onKey}
          onBlur={() => setTimeout(() => { setSugg([]); commit(true); }, 150)}
          placeholder={chips.length === 0 ? placeholder : ""}
          autoComplete="off"
          data-testid={testid}
        />
        {sugg.length > 0 && (
          <div className="sugg-list" data-testid={`${testid}-suggestions`}>
            {sugg.map((c, i) => (
              <button
                key={c.email}
                type="button"
                className={`sugg-item ${i === idx ? "active" : ""}`}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => aggiungi(c.email)}
                data-testid={`${testid}-suggestion-${i}`}
              >
                {c.nome && <span className="sugg-nome">{c.nome}</span>}
                <span className="sugg-email">{c.email}</span>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default function ComposeModal({ open, onClose, onSend, onAutosave, initial, accounts = [], defaultAccountId }) {
  const [fromId, setFromId] = useState("");
  const [dest, setDest] = useState([]);
  const [destInput, setDestInput] = useState("");
  const [cc, setCc] = useState([]);
  const [ccInput, setCcInput] = useState("");
  const [ccn, setCcn] = useState([]);
  const [ccnInput, setCcnInput] = useState("");
  const [oggetto, setOggetto] = useState("");
  // testo = messaggio dell'utente in testo semplice (per AI, bozze, parte text/plain); html = stesso testo formattato (grassetto/colori)
  const [testo, setTesto] = useState("");
  const [html, setHtml] = useState("");
  const corpoRef = useRef(null);
  const cronoRef = useRef(null);
  // Cronologia della conversazione (Rispondi/Inoltra): area separata SOTTO firma e logo, così il logo si vede sotto la firma
  const [cronologia, setCronologia] = useState("");
  const [mostraCrono, setMostraCrono] = useState(false); // nascosta dietro l'icona; resta comunque nella mail inviata
  const corpoIntero = (t, c) => (c && c.trim() ? `${(t || "").replace(/\s+$/, "")}\n\n${c.trim()}` : t || "");
  const impostaTesto = (t) => {
    setTesto(t);
    setHtml(t ? testoAHtml(t) : "");
  };
  // la cronologia cresce col contenuto: scorre tutto il modulo insieme (testo, firma, logo, cronologia)
  const adatta = (el) => {
    if (!el) return;
    el.style.height = "auto";
    el.style.height = Math.max(60, el.scrollHeight + 2) + "px";
  };
  useEffect(() => {
    adatta(cronoRef.current);
  }, [cronologia, open, mostraCrono]);
  const [showCc, setShowCc] = useState(false);
  const [showCcn, setShowCcn] = useState(false);
  const [traccia, setTraccia] = useState(true);
  const [allegati, setAllegati] = useState([]);
  const [loadingAI, setLoadingAI] = useState(null); // 'traduci' | 'ai' | null
  const [aiMenu, setAiMenu] = useState(false);
  const [chiediBozza, setChiediBozza] = useState(false);
  const fileRef = useRef(null);
  // chiave di idempotenza dell'invio: una per apertura del modulo (riusata se il modulo si riapre dopo un errore di rete)
  const clientId = useRef("");
  const firma = accounts.find((a) => a.id === fromId);
  // corpo in testo semplice come sempre: messaggio + firma ("--") della casella scelta (il server la riconosce e non la ripete)
  const corpo = testo.replace(/\s+$/, "") + bloccoFirma(firma);

  useEffect(() => {
    if (open) {
      const split = (s) => (s || "").split(/[,;]+/).map((x) => x.trim()).filter((x) => x.includes("@"));
      const def = accounts.find((a) => a.id === initial?.account_id) || accounts.find((a) => a.id === defaultAccountId) || accounts[0];
      setFromId(def?.id || "");
      setDest(split(initial?.a));
      setDestInput("");
      setCc(split(initial?.cc));
      setCcInput("");
      setCcn(split(initial?.ccn));
      setCcnInput("");
      setShowCc(!!initial?.cc);
      setShowCcn(!!initial?.ccn);
      setOggetto(initial?.oggetto || "");
      let base = initial?.corpo || "";
      let crono = initial?.citazione || "";
      const k = base.indexOf(SEP_CRONO);
      if (k >= 0) {
        // bozza riaperta: la cronologia torna nella sua area
        crono = base.slice(k + 1).trim();
        base = base.slice(0, k).replace(/\s+$/, "");
      }
      // firma e cronologia eventualmente presenti nel testo (bozze) vengono tolte: la firma è mostrata sotto l'editor
      const t0 = spezzaCorpo(base).testo.replace(/\s+$/, "");
      setTesto(t0);
      setHtml(initial?.corpo_html || (t0 ? testoAHtml(t0) : ""));
      setCronologia(crono);
      setMostraCrono(false);
      setTimeout(() => {
        const el = corpoRef.current;
        if (el) {
          el.focus();
          const sel = window.getSelection();
          const r = document.createRange();
          r.setStart(el, 0);
          r.collapse(true);
          sel.removeAllRanges();
          sel.addRange(r);
        }
      }, 50);
      setAllegati(initial?.allegati || []);
      setLoadingAI(null);
      setAiMenu(false);
      setChiediBozza(false);
      clientId.current = initial?.client_id || (window.crypto?.randomUUID ? window.crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const raccogli = (chips, pending) => {
    const all = [...chips];
    const v = pending.trim().replace(/[,;\s]+$/, "");
    if (v && v.includes("@")) all.push(v);
    return all;
  };

  const invia = (bozza) => {
    const toAll = raccogli(dest, destInput);
    const ccAll = raccogli(cc, ccInput);
    const ccnAll = raccogli(ccn, ccnInput);
    if (!bozza && toAll.length === 0) {
      toast.error("Inserisci almeno un destinatario");
      return;
    }
    clearInterval(autosaveTimer.current);
    onSend({
      account_id: fromId,
      a: toAll.join(", "),
      cc: ccAll.join(", "),
      ccn: ccnAll.join(", "),
      oggetto,
      corpo: corpoIntero(corpo, cronologia),
      corpo_html: htmlVuoto(html) ? "" : html,
      bozza,
      allegati,
      draft_id: draftId.current || "",
      client_id: clientId.current,
      traccia,
    });
  };

  // Salvataggio automatico della bozza ogni 30 s mentre si scrive (stessa bozza aggiornata, non duplicati)
  const draftId = useRef("");
  const autosaveTimer = useRef(null);
  const statoRef = useRef(null);
  statoRef.current = { fromId, dest, destInput, cc, ccInput, ccn, ccnInput, oggetto, corpo: corpoIntero(corpo, cronologia), corpo_html: htmlVuoto(html) ? "" : html, allegati };
  const ultimoSalvato = useRef("");
  useEffect(() => {
    draftId.current = initial?.draft_id || "";
    ultimoSalvato.current = "";
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);
  const onAutosaveRef = useRef(onAutosave);
  onAutosaveRef.current = onAutosave;
  useEffect(() => {
    if (!open || !onAutosaveRef.current) return;
    autosaveTimer.current = setInterval(() => {
      const s = statoRef.current;
      const payload = {
        id: draftId.current,
        account_id: s.fromId,
        a: raccogli(s.dest, s.destInput).join(", "),
        cc: raccogli(s.cc, s.ccInput).join(", "),
        ccn: raccogli(s.ccn, s.ccnInput).join(", "),
        oggetto: s.oggetto,
        corpo: s.corpo,
        corpo_html: s.corpo_html,
        allegati: s.allegati,
      };
      const pieno = payload.a || payload.cc || payload.ccn || payload.oggetto.trim() || payload.corpo.trim() || payload.allegati.length;
      const firma = JSON.stringify({ ...payload, id: "" });
      if (!pieno || firma === ultimoSalvato.current) return;
      ultimoSalvato.current = firma;
      Promise.resolve(onAutosaveRef.current(payload)).then((b) => {
        if (b?.id) draftId.current = b.id;
      });
    }, 30000);
    return () => clearInterval(autosaveTimer.current);
  }, [open]);

  // RIFIUTA / chiudi: se c'è qualcosa scritto, chiedi se salvare in bozze
  const sporco =
    dest.length > 0 || destInput.trim() || cc.length > 0 || ccn.length > 0 || oggetto.trim() || !htmlVuoto(html) || allegati.length > 0;
  const rifiuta = () => {
    if (sporco) {
      setChiediBozza(true);
      return;
    }
    onClose();
  };
  const scarta = () => {
    setChiediBozza(false);
    clearInterval(autosaveTimer.current);
    onClose(draftId.current ? { scartaBozza: draftId.current } : undefined);
    toast("Email scartata");
  };
  const salvaEChiudi = () => {
    setChiediBozza(false);
    invia(true);
  };

  // AGGIUNGI ALLEGATO
  const MAX_TOTAL = 100 * 1024 * 1024;
  const leggiFile = (f) =>
    new Promise((resolve, reject) => {
      const r = new FileReader();
      r.onload = () => resolve(String(r.result).split(",")[1] || "");
      r.onerror = reject;
      r.readAsDataURL(f);
    });

  const scegliFile = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (files.length === 0) return;
    const attuale = allegati.reduce((a, f) => a + (f.bytes || 0), 0);
    const nuovi = files.reduce((a, f) => a + f.size, 0);
    if (attuale + nuovi > MAX_TOTAL) {
      toast.error("Allegati troppo grandi: massimo 100 MB in totale");
      return;
    }
    const letti = [];
    for (const f of files) {
      letti.push({ nome: f.name, dimensione: formatSize(f.size), bytes: f.size, tipo: f.type || "application/octet-stream", dati: await leggiFile(f) });
    }
    setAllegati((prev) => [...prev, ...letti]);
    toast.success(files.length + " allegato/i aggiunto/i");
  };

  // Il testo dell'AI compare parola per parola mentre arriva (al massimo un aggiornamento ogni 60 ms)
  const direttaRef = useRef(0);
  const inDiretta = (parziale) => {
    const ora = Date.now();
    if (ora - direttaRef.current < 60) return;
    direttaRef.current = ora;
    impostaTesto(parziale);
  };

  // TRADUCI (AI reale)
  const traduci = async () => {
    if (!testo.trim()) {
      toast("Scrivi prima il messaggio da tradurre");
      return;
    }
    setLoadingAI("traduci");
    try {
      // in risposta/inoltro traduce nella lingua del mittente (rilevata dall'email originale); altrimenti rileva la lingua e traduce
      const inRisposta = !!cronologia.trim();
      impostaTesto((await api.aiActionStream(inRisposta ? "traduci_risposta" : "traduci", testo, oggetto, inRisposta ? cronologia : "", inDiretta)).replace(/\s+$/, ""));
      toast.success(inRisposta ? "Tradotto nella lingua del mittente" : "Messaggio tradotto");
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || "L'AI non è al momento disponibile");
    } finally {
      setLoadingAI(null);
    }
  };

  // AI: varianti di risposta (AI reale)
  const applyAI = async (mode) => {
    if (mode !== "scrivi" && !testo.trim()) {
      toast("Scrivi prima il messaggio da elaborare");
      return;
    }
    setLoadingAI(mode === "scrivi" ? "scrivi" : "ai");
    try {
      impostaTesto((await api.aiActionStream(mode, testo, oggetto, cronologia, inDiretta)).replace(/\s+$/, ""));
      toast.success("Testo aggiornato dall'AI");
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || "L'AI non è al momento disponibile");
    } finally {
      setLoadingAI(null);
    }
  };

  // STAMPA
  const stampa = () => {
    toast("Preparazione stampa...");
    setTimeout(() => window.print(), 300);
  };

  return (
    <div className={`compose-window ${open ? "show" : ""}`} data-testid="compose-modal">
      {aiMenu && <div className="dd-backdrop" onClick={() => setAiMenu(false)} />}
      {chiediBozza && (
        <div className="wf-modal-backdrop show compose-confirm-backdrop" onClick={() => setChiediBozza(false)}>
          <div className="wf-modal compose-confirm" onClick={(e) => e.stopPropagation()} data-testid="compose-draft-confirm">
            <div className="wf-modal-head">
              <span className="wf-modal-title">
                <Save size={19} strokeWidth={2.3} /> Salvare in bozze?
              </span>
            </div>
            <div className="compose-confirm-text">Hai un'email non inviata. Vuoi salvarla nelle bozze prima di uscire?</div>
            <div className="wf-modal-actions compose-confirm-actions">
              <button className="wf-btn" onClick={() => setChiediBozza(false)} data-testid="compose-draft-cancel">
                Annulla
              </button>
              <button className="wf-btn del-profile-btn" onClick={scarta} data-testid="compose-draft-discard">
                <X size={16} strokeWidth={2.6} /> Scarta
              </button>
              <button className="wf-btn primary" onClick={salvaEChiudi} data-testid="compose-draft-save">
                <Save size={16} strokeWidth={2.4} /> Salva bozza
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Barra superiore: TRADUCI, AI, ACCETTA, RIFIUTA */}
      <div className="topbar compose-topbar">
        <button className="icon-btn icon-circle back-btn compose-back" onClick={rifiuta} aria-label="Indietro" title="Torna indietro" data-testid="compose-back">
          <ArrowLeft size={28} strokeWidth={2.8} />
        </button>
        <span className="wf-modal-title compose-title" data-testid="compose-title">
          <SquarePen size={28} strokeWidth={2.5} /> Scrivi
        </span>
        <div style={{ flex: 1 }} />
        <button
          className="icon-btn translate-btn"
          title="Traduci"
          aria-label="Traduci"
          onClick={traduci}
          disabled={!!loadingAI}
          data-testid="compose-translate-btn"
        >
          <Languages size={26} strokeWidth={2.5} />
        </button>
        <div className="anchor-wrap">
          <button
            className="icon-btn ai-btn"
            title="AI - varianti di risposta"
            aria-label="AI"
            onClick={() => setAiMenu((m) => !m)}
            disabled={!!loadingAI}
            data-testid="compose-ai-btn"
          >
            <Sparkles size={25} strokeWidth={2.4} fill="currentColor" />
          </button>
          {aiMenu && (
            <div className="dropdown align-right" data-testid="compose-ai-menu">
              {[
                { id: "scrivi", label: "SCRIVI TU" },
                { id: "riduci", label: "RIDUCI" },
                { id: "espandi", label: "ESPANDI" },
                { id: "formale", label: "FORMALE" },
                { id: "amichevole", label: "AMICHEVOLE" },
                { id: "correggi", label: "CORREGGI" },
              ].map((v) => (
                <button
                  key={v.id}
                  className="chip ai-variant dd-item"
                  onClick={() => {
                    setAiMenu(false);
                    applyAI(v.id);
                  }}
                  data-testid={`compose-ai-${v.id}`}
                >
                  {v.label}
                </button>
              ))}
            </div>
          )}
        </div>
        <button
          className="icon-btn x-esci con-tip"
          data-tip="Chiudi"
          aria-label="Chiudi"
          onClick={rifiuta}
          data-testid="compose-reject-btn"
        >
          <X size={23} strokeWidth={3} />
        </button>
      </div>

      <div className="compose-content">
        <div className="compose-intestazione" data-testid="compose-intestazione">
        <div className="wf-field">
          <label className="wf-label">Mittente</label>
          {accounts.length > 0 ? (
            <select
              className="wf-input compose-from"
              value={fromId}
              onChange={(e) => setFromId(e.target.value)}
              data-testid="compose-from"
            >
              {accounts.map((a) => (
                <option key={a.id} value={a.id}>
                  {`${a.email}${a.tipo === "pec" ? " (PEC)" : ""}${accounts.some((b) => b.id !== a.id && b.email === a.email) ? ` · ${a.provider_nome || a.provider || a.imap_host || a.label}` : ""}`}
                </option>
              ))}
            </select>
          ) : (
            <div className="compose-from-empty" data-testid="compose-from-empty">
              Nessun account collegato: aggiungine uno dalla tendina account
            </div>
          )}
        </div>
        <RecipientField
          label="Destinatario"
          chips={dest}
          setChips={setDest}
          value={destInput}
          setValue={setDestInput}
          placeholder="destinatario@email.com (Invio per aggiungerne altri)"
          testid="compose-to"
          extra={
            <span className="cc-toggles">
              <button
                className={`cc-toggle ${showCc ? "on" : ""}`}
                onClick={() => setShowCc((v) => !v)}
                data-testid="toggle-cc"
              >
                CC
              </button>
              <button
                className={`cc-toggle ${showCcn ? "on" : ""}`}
                onClick={() => setShowCcn((v) => !v)}
                data-testid="toggle-ccn"
              >
                CCN
              </button>
              <button
                className={`cc-toggle traccia-toggle ${traccia ? "on" : ""}`}
                onClick={() => setTraccia((v) => !v)}
                title={traccia ? "Tracciamento lettura attivo: saprai quando il destinatario apre l'email" : "Tracciamento lettura disattivato"}
                data-testid="toggle-traccia"
              >
                {traccia ? <Eye size={13} strokeWidth={2.6} /> : <EyeOff size={13} strokeWidth={2.4} />} LETTURA
              </button>
            </span>
          }
        />
        {showCc && (
          <RecipientField
            label="CC"
            chips={cc}
            setChips={setCc}
            value={ccInput}
            setValue={setCcInput}
            placeholder="cc@email.com"
            testid="compose-cc"
          />
        )}
        {showCcn && (
          <RecipientField
            label="CCN"
            chips={ccn}
            setChips={setCcn}
            value={ccnInput}
            setValue={setCcnInput}
            placeholder="ccn@email.com"
            testid="compose-ccn"
          />
        )}

        <div className="wf-field">
          <label className="wf-label">Oggetto</label>
          <input
            className="wf-input"
            placeholder="Oggetto dell'email"
            value={oggetto}
            onChange={(e) => setOggetto(e.target.value)}
            data-testid="compose-subject"
          />
        </div>
        </div>
        <div className="wf-field compose-msg-field">
          <label className="wf-label">Messaggio</label>
          <EditorRicco
            value={html}
            editorRef={corpoRef}
            placeholder="Scrivi il tuo messaggio..."
            onChange={(h, t) => {
              setHtml(h);
              setTesto(t);
            }}
          />
          {firma && (firma.firma_testo || firma.firma_img) && (
            <div className={`compose-firma-blocco pos-${firma.firma_img_pos || "sotto"}`} title="Firma della casella scelta (modificabile da Impostazioni → Firme)" data-testid="compose-firma-inline">
              <div className="compose-firma-sep">--</div>
              <div className="compose-firma-corpo">
                {firma.firma_testo && <div className="compose-firma-inline">{firma.firma_testo.trim()}</div>}
                {firma.firma_img && (
                  <div className="compose-logo" data-testid="compose-signature-preview">
                    {firma.firma_link ? (
                      <a href={firma.firma_link} target="_blank" rel="noopener noreferrer" title={firma.firma_link} data-testid="compose-signature-logo-link">
                        <img src={firma.firma_img} alt="Logo firma" className="compose-firma-img" style={{ width: firma.firma_img_larg || 130 }} />
                      </a>
                    ) : (
                      <img src={firma.firma_img} alt="Logo firma" className="compose-firma-img" style={{ width: firma.firma_img_larg || 130 }} />
                    )}
                  </div>
                )}
              </div>
            </div>
          )}
          {cronologia && (
            <div className="compose-crono-wrap">
              <button
                type="button"
                className={`compose-crono-btn ${mostraCrono ? "on" : ""}`}
                onClick={() => setMostraCrono((v) => !v)}
                title={mostraCrono ? "Nascondi la cronologia della conversazione" : "Mostra la cronologia della conversazione (viene comunque inviata)"}
                aria-label="Cronologia della conversazione"
                data-testid="compose-crono-toggle"
              >
                <FileClock size={22} strokeWidth={2.2} />
                <span>{mostraCrono ? "Nascondi cronologia" : "Cronologia"}</span>
              </button>
              {mostraCrono && (
                <textarea
                  ref={cronoRef}
                  className="wf-textarea compose-body-auto compose-crono"
                  value={cronologia}
                  onChange={(e) => setCronologia(e.target.value)}
                  aria-label="Comunicazione precedente"
                  data-testid="compose-cronologia"
                />
              )}
            </div>
          )}
        </div>

        {/* Allegati aggiunti */}
        {allegati.length > 0 && (
          <div className="compose-attachments" data-testid="compose-attachments">
            {allegati.map((f, i) => (
              <span key={f.upload_id || `${f.nome}-${f.dimensione || i}`} className="attach-chip" data-testid={`compose-attachment-${i}`}>
                <Paperclip size={13} strokeWidth={2.4} />
                <span className="attach-chip-name">{f.nome}</span>
                <span style={{ color: "var(--ink-soft)", fontSize: 11 }}>{f.dimensione}</span>
                <button
                  className="attach-chip-x"
                  onClick={() => setAllegati((prev) => prev.filter((_, j) => j !== i))}
                  aria-label="Rimuovi allegato"
                >
                  <X size={13} strokeWidth={2.6} />
                </button>
              </span>
            ))}
          </div>
        )}

        {loadingAI && (
          <div style={{ marginBottom: 10 }} data-testid="compose-ai-loading">
            <AiRagiona tipo={["traduci", "scrivi"].includes(loadingAI) ? loadingAI : "elabora"} />
          </div>
        )}
      </div>

      {/* Barra strumenti in basso: allegato, stampa, bozza */}
      <div className="compose-tools">
        <input type="file" ref={fileRef} onChange={scegliFile} multiple style={{ display: "none" }} data-testid="compose-file-input" />
        <button
          className="icon-btn"
          title="Aggiungi allegato"
          aria-label="Aggiungi allegato"
          onClick={() => fileRef.current?.click()}
          data-testid="compose-attach-btn"
        >
          <Paperclip size={21} strokeWidth={2.3} />
        </button>
        <button
          className="icon-btn"
          title="Stampa"
          aria-label="Stampa"
          onClick={stampa}
          data-testid="compose-print-btn"
        >
          <Printer size={21} strokeWidth={2.3} />
        </button>

        <div style={{ flex: 1 }} />

        <button className="wf-btn" onClick={() => invia(true)} data-testid="compose-save-draft">
          <Save size={16} strokeWidth={2.3} /> Bozza
        </button>
        <button className="wf-btn send-btn" onClick={() => invia(false)} data-testid="compose-send-btn">
          <Send size={16} strokeWidth={2.3} /> Invia
        </button>
      </div>
    </div>
  );
}
